import type { ProductPillColor } from "@/components/cinch/ProductStatePill";

import type { WhitespaceCustomer, WhitespaceGroup } from "./api";

/** Backend group colors are DS palette tokens; anything unknown falls back
 *  to brand so a bad config value can't break the page. */
export function pillColor(color: string): ProductPillColor {
  const known: ProductPillColor[] = ["brand", "power", "purple", "success", "orange", "teal"];
  return (known as string[]).includes(color) ? (color as ProductPillColor) : "brand";
}

/** bg-* utility per palette token (static map so Tailwind sees the classes). */
export const BAR_CLASS: Record<ProductPillColor, string> = {
  brand: "bg-brand",
  power: "bg-power",
  purple: "bg-purple",
  success: "bg-success",
  orange: "bg-orange",
  teal: "bg-teal",
};

export type SortKey = "op" | "name" | "newest" | "tier" | "arr";

export interface WhitespaceFilters {
  q: string;
  /** Selected tiers (1–5); empty = all. */
  tiers: number[];
  /** CSM token (owner:… / user:… / partner:…); null = all. */
  csm: string | null;
  /** Group key — only customers MISSING that product (KPI-card click). */
  missing: string | null;
  /** Include legacy / non-platform customers. */
  legacy: boolean;
  /** Only customers with at least one open upsell. */
  openOnly: boolean;
}

export const DEFAULT_FILTERS: WhitespaceFilters = {
  q: "",
  tiers: [],
  csm: null,
  missing: null,
  legacy: false,
  openOnly: false,
};

/** Open upsells = tracked products with no billing and no contract. */
export function openCount(c: WhitespaceCustomer, groupKeys: string[]): number {
  return groupKeys.reduce((n, k) => n + (c.products[k] === "none" ? 1 : 0), 0);
}

export function filterCustomers(
  customers: WhitespaceCustomer[],
  groups: WhitespaceGroup[],
  f: WhitespaceFilters,
): WhitespaceCustomer[] {
  const keys = groups.map((g) => g.key);
  const q = f.q.trim().toLowerCase();
  return customers.filter((c) => {
    if (!f.legacy && !c.platform) return false;
    if (q && !(c.name ?? "").toLowerCase().includes(q)) return false;
    if (f.tiers.length && !(c.tier != null && f.tiers.includes(c.tier))) return false;
    if (f.csm && c.csm_token !== f.csm) return false;
    if (f.missing && c.products[f.missing] !== "none") return false;
    if (f.openOnly && openCount(c, keys) === 0) return false;
    return true;
  });
}

const byName = (a: WhitespaceCustomer, b: WhitespaceCustomer) =>
  (a.name ?? "").localeCompare(b.name ?? "");

export function sortCustomers(
  customers: WhitespaceCustomer[],
  groups: WhitespaceGroup[],
  sort: SortKey,
): WhitespaceCustomer[] {
  const keys = groups.map((g) => g.key);
  const out = [...customers];
  switch (sort) {
    case "name":
      return out.sort(byName);
    case "newest":
      return out.sort(
        (a, b) => (b.since_year ?? 0) - (a.since_year ?? 0) || byName(a, b),
      );
    case "tier":
      // T1 first; untiered last; richer first within a tier.
      return out.sort(
        (a, b) => (a.tier ?? 99) - (b.tier ?? 99) || b.arr - a.arr || byName(a, b),
      );
    case "arr":
      return out.sort((a, b) => b.arr - a.arr || byName(a, b));
    case "op":
    default:
      return out.sort(
        (a, b) => openCount(b, keys) - openCount(a, keys) || byName(a, b),
      );
  }
}

export interface GroupStats {
  /** Customers attached (billing or on contract). */
  attached: number;
  /** Customers actually billing. */
  billed: number;
  total: number;
  /** Attach rate %, attached / total (0 when the pool is empty). */
  attachPct: number;
  /** Open whitespace count. */
  whitespace: number;
}

export function groupStats(customers: WhitespaceCustomer[], key: string): GroupStats {
  const total = customers.length;
  let attached = 0;
  let billed = 0;
  for (const c of customers) {
    const state = c.products[key];
    if (state === "billed") {
      billed += 1;
      attached += 1;
    } else if (state === "contracted") {
      attached += 1;
    }
  }
  return {
    attached,
    billed,
    total,
    attachPct: total ? Math.round((attached / total) * 100) : 0,
    whitespace: total - attached,
  };
}

export function totalOpenUpsells(
  customers: WhitespaceCustomer[],
  groups: WhitespaceGroup[],
): number {
  const keys = groups.map((g) => g.key);
  return customers.reduce((sum, c) => sum + openCount(c, keys), 0);
}
